import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { FeatureFlag } from '@ps/web-core-enums';
import type { KBAQuestionType } from '@ps/web-core-store';
import { CashierPath } from '@ps/web-pam-types';
import { useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';

import { Country } from '@swf/core/enums';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import type { Store } from '@swf/core/store';
import { getState, select } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';

import useHasMitIdDataContext from '../../../hooks/useHasMitIdDataContext';
import useMitIdDataContext from '../../../hooks/useMitIdDataContext';
import { CashierFlowType } from '../../../types/Cashier';
import type { RealMoneyInformation } from '../../../types/Cashier';
import { RealMoneyStatus, VerificationErrorCode } from '../../../types/status';
import { RMF_TRIGGER_POINT } from '../utils/rmf';

export enum Guard {
  CheckRMSuspended = 'checkRMSuspended',
  CheckUserFlags = 'checkUserFlags',
  DoesJumpToFeatureStep = 'doesJumpToFeatureStep',
  PhoneVerificationCheck = 'phoneVerificationCheck',
  RmfChallengeQuestionsCheck = 'rmfChallengeQuestionsCheck',
  RmfDobEnabled = 'rmfDobEnabled',
  ShowCitizenship = 'showCitizenship',
  ShowPendingVerification = 'showPendingVerification',
  SignupDepositEnabled = 'signupDepositEnabled',
  PhoneCollectionCheck = 'phoneCollectionCheck',
  GoToDepositLimits = 'goToDepositLimits',
  LimitsSelected = 'limitsSelected',
  SingleSignUpEnabled = 'singleSignUpEnabled',
  ShowSumSubFirst = 'sumSubFirst',
  CheckSumSubVerification = 'checkSumSubVerification',
  isCashierPath = 'isCashierPath',
  CheckRetryUserFlags = 'checkRetryUserFlags',
  CheckIsDEUserRMOK = 'checkIsDEUserRMOK',
  RecheckUserFlags = 'recheckUserFlags',
  IsRetryScreenEnabled = 'isRetryScreenEnabled',
  UserStatusInSpain = 'userStatusInSpain',
  CheckUserAccountStatus = 'checkUserAccountStatus',
  checkPoker = 'checkPoker',
  checkCasino = 'checkCasino',
  showSGScreen = 'showSGScreen',
  CheckSGCScreenAndSelection = 'checkSGCScreenAndSelection',
  StandaloneRmf = 'standaloneRmf',
  HideRMFCompletion = 'hideRMFCompletion',
  WrmfKBA = 'wrmfKBA',
  CheckEKYCFailed = 'checkEKYCFailed',
  CheckEKYCRetry = 'checkEKYCRetry',
  doesJumpFromDepositProcess = 'doesJumpFromDepositProcess',
  ShowWelcomeBonusES = 'showWelcomeBonusES',
  TooManyEKYCRequests = 'tooManyEKYCRequests',
  CheckMobilePhoneVerified = 'checkMobilePhoneVerified',
  StandaloneRMFMitIdDataPresent = 'standaloneRMFMitIdDataPresent',
  MitIdDataPresent = 'mitIdDataPresent',
  ShouldShowValidationScreenDK = 'shouldShowValidationScreenDK',
  ShowDKVerificationScreen = 'showDKVerificationScreen',
  IsSSNConfirmationRequired = 'isSSNConfirmationRequired'
}

const useRMFGuards = () => {
  const store = useStore() as Store;
  const hideBonusCode = useSelector(select.account.hideBonusCode);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const query = useSelector(select.location.getQuery);
  const isStandaloneRmf = useSelector(select.location.isStandAloneRMF);
  const hasMitIdData = useMitIdDataContext(CashierPath.RealMoneyForm);
  const hasRmfMitIdDataContext = useHasMitIdDataContext();

  const {
    data: { cashierFlow }
  } = useWizardContext();

  const doesJumpToFeatureStep = useCallback(
    ({
      data: { cashierFlow, rmfTargetToJump }
    }: {
      data: { cashierFlow: CashierFlowType; rmfTargetToJump?: string };
    }): boolean =>
      [CashierFlowType.FirstWithdraw, CashierFlowType.FirstP2pTransfer].includes(cashierFlow) ||
      !!rmfTargetToJump,
    []
  );

  const doesJumpFromDepositProcess = useCallback(
    ({ data: { rmfTargetToJump } }: { data: { rmfTargetToJump?: string } }): boolean =>
      !!rmfTargetToJump &&
      [
        '#deposit.deposit-process',
        '#withdraw.get-cashout-options',
        '#withdraw.withdraw-flow-check'
      ].includes(rmfTargetToJump),
    []
  );

  const showWelcomeBonusES = useCallback(
    ({
      data: { crmBonusCode, signupPassed }
    }: {
      data: {
        crmBonusCode: string;
        signupPassed: boolean;
      };
    }): boolean => {
      const isSWCWelcomeBonusScreenEnabled = select.feature.check(
        getState(),
        FeatureFlag.SwcWelcomeBonusScreen
      );
      const isSWCAffiliateBonusEnabled = select.feature.check(
        getState(),
        FeatureFlag.SwcAffiliateBonus
      );
      const isSwcRmfBonusScreenEnabled = select.feature.check(
        getState(),
        FeatureFlag.SwcRmfBonusScreen
      );

      const showWelcomeBonus =
        isSwcRmfBonusScreenEnabled &&
        isSWCWelcomeBonusScreenEnabled &&
        !!(isSWCAffiliateBonusEnabled || crmBonusCode) &&
        !hideBonusCode &&
        !!signupPassed;

      return showWelcomeBonus;
    },

    [hideBonusCode]
  );
  const rmfDobEnabled = useCallback(
    ({
      data: {
        userInfo: { dateOfBirth }
      }
    }: {
      data: { userInfo: { dateOfBirth: string } };
    }) => !dateOfBirth,
    []
  );

  const rmfChallengeQuestionsCheck = useCallback(
    ({
      data: { realMoneyInformation }
    }: {
      data: { realMoneyInformation: RealMoneyInformation | null };
    }) => !!realMoneyInformation?.securityQuestions,
    []
  );

  const signupDepositEnabled = () =>
    select.feature.check(store.getState(), FeatureFlag.SwcSignupDeposit);

  const checkUserFlags = useCallback(() => {
    const rootState = store.getState();
    const isKYCRequired = store.select.account.isKYCRequired(rootState);
    const isAuthVerified = store.select.account.isAuthVerified(rootState);
    const isAgeVerified = store.select.account.isAgeVerified(rootState);
    const isRealMoneyOK = store.select.account.isRealMoneyOK(rootState);

    return (
      isKYCRequired || (!isKYCRequired && (!isAuthVerified || !isAgeVerified || !isRealMoneyOK))
    );
  }, [store]);

  const checkEKYCRetry = (context: WizardContext) => {
    const code = context.error?.globalErrors?.[0].code;

    return code === VerificationErrorCode.ElectronicVerificationFailed;
  };

  const checkEKYCFailed = (context: WizardContext) => {
    const code = context.error?.globalErrors?.[0].code;

    return (
      code === VerificationErrorCode.ElectronicVerificationFailed ||
      code === VerificationErrorCode.ElectronicVerificationFailedTooManyRequests
    );
  };

  const tooManyEKYCRequests = (context: WizardContext) => {
    const code = context.error?.globalErrors?.[0].code;

    return code === VerificationErrorCode.ElectronicVerificationFailedTooManyRequests;
  };

  const checkRetryUserFlags = useCallback(() => {
    const rootState = store.getState();
    const isAuthVerified = store.select.account.isAuthVerified(rootState);
    const isAgeVerified = store.select.account.isAgeVerified(rootState);
    const isRealMoneyOK = store.select.account.isRealMoneyOK(rootState);

    return !isAuthVerified || !isAgeVerified || !isRealMoneyOK;
  }, [store]);

  const checkIsDEUserRMOK = useCallback(() => {
    const rootState = store.getState();
    const isRealMoneyOK = store.select.account.isRealMoneyOK(rootState);
    const isRetryScreenEnabled = select.feature.check(store.getState(), FeatureFlag.SwcRetryScreen);

    return isRealMoneyOK && isRetryScreenEnabled;
  }, [store]);

  const checkUserAccountStatus = useCallback(() => {
    const rootState = store.getState();
    const isRealMoneyOK = store.select.account.isRealMoneyOK(rootState);
    const isAuthVerified = store.select.account.isAuthVerified(rootState);

    return isRealMoneyOK && isAuthVerified;
  }, [store]);

  const showPendingVerification = useCallback(
    () => !store.select.account.isKYCRequired(store.getState()),
    [store]
  );

  const checkMobilePhoneVerified = useCallback(
    () => store.select.account.isMobileVerified(store.getState()),
    [store]
  );

  const checkRMSuspended = useCallback(
    () => store.select.account.isSuspendRM(store.getState()),
    [store]
  );

  const phoneVerificationCheck = useCallback(
    () => select.feature.check(store.getState(), FeatureFlag.SwcRMFPhoneVerification),
    [store]
  );

  const phoneCollectionCheck = useCallback(
    () => select.feature.check(store.getState(), FeatureFlag.SwcSignupPhoneCollection),
    [store]
  );

  const singleSignUpEnabled = useCallback(
    () => select.feature.check(store.getState(), FeatureFlag.SwcSignupOnePage),
    [store]
  );

  const isCashierPath = useCallback(
    ({ data: { cashierFlow } }: { data: { cashierFlow: CashierFlowType } }) =>
      [CashierFlowType.FirstDeposit, CashierFlowType.FirstWithdraw].includes(cashierFlow),
    []
  );

  const showLimitsScreen = useCallback(
    ({ values: { userLimitsSelect } }: Pick<WizardContext, 'values'>) => userLimitsSelect?.skipped,
    []
  );

  const showDKVerificationScreen = useCallback(
    ({ values: { citizenship } }: Pick<WizardContext, 'values'>) =>
      ![Country.Denmark, Country.Greenland].includes(citizenship),
    []
  );

  const checkSGCScreenAndSelection = useCallback(
    ({ values: { userLimitsSelect } }: Pick<WizardContext, 'values'>) =>
      userLimitsSelect.skipped && !!select.feature.check(store.getState(), FeatureFlag.SwcSGScreen),
    [store]
  );

  const showSumSubFirst = useCallback(
    ({ values: { country } }: Pick<WizardContext, 'values'>) =>
      country === Country.Brazil &&
      select.feature.check(store.getState(), FeatureFlag.SwcSignupRmfSumSub),
    [store]
  );

  const checkSumSubVerification = useCallback(
    ({ values: { sumSubVerification } }: Pick<WizardContext, 'values'>) =>
      sumSubVerification.pased && (!CashierFlowType.FirstWithdraw.includes(cashierFlow) as boolean),
    [cashierFlow]
  );

  const retryScreenEnabled = useCallback(
    () => select.feature.check(store.getState(), FeatureFlag.SwcRetryScreen),
    [store]
  );

  const kbaScreenEnabled = useCallback(
    ({ data: { kbaQuestions } }: { data: { kbaQuestions: KBAQuestionType[] } }) =>
      !!select.feature.check(store.getState(), FeatureFlag.WrmfKBA) && !!kbaQuestions?.length,
    [store]
  );

  const checkCasino = useCallback(() => {
    const rootState = store.getState();
    const { registrationBrand } = select.localSettings.get(rootState);
    return registrationBrand === BrandTypes.STARS_CASINO;
  }, [store]);

  const checkPoker = useCallback(() => {
    const rootState = store.getState();
    const { registrationBrand } = select.localSettings.get(rootState);
    return !registrationBrand || registrationBrand === BrandTypes.POKER_STARS;
  }, [store]);

  const shouldShowSGScreen = useCallback(
    () => select.feature.check(store.getState(), FeatureFlag.SwcSGScreen),
    [store]
  );

  const showCitizenship = useCallback(
    ({ data: { userInfoFields } }: Pick<WizardContext, 'data'>) => {
      const field = userInfoFields?.find(({ name }: { name: string }) => name === 'citizenship');
      const skipCitizenship = !!field && !!field.value && !field.editable;

      return !skipCitizenship;
    },
    []
  );

  const userStatusInSpain = useCallback(
    ({ values: { country, citizenship } }: Pick<WizardContext, 'values'>) =>
      country === Country.Spain || citizenship === Country.Spain,
    []
  );

  const standaloneRMF = useCallback(() => {
    if (isStandaloneRmf && isDesktopEmbedded && !!query?.TriggerPoint) {
      return Number(query?.TriggerPoint) !== RMF_TRIGGER_POINT.TriggerPoint_AccountCreation;
    }
    return isStandaloneRmf;
  }, [isDesktopEmbedded, isStandaloneRmf, query?.TriggerPoint]);

  const mitIdDataPresent = ({ data }: WizardContext) => hasRmfMitIdDataContext({ data });

  const standaloneRMFMitIdDataPresent = () => hasMitIdData;

  const shouldShowValidationScreenDK = (context: WizardContext) => {
    const code = context.error?.globalErrors?.[0].code;

    return (
      code === RealMoneyStatus.NOT_ALLOWED ||
      code === RealMoneyStatus.RM_DATA_REJECTED_BY_REGULATOR ||
      code === RealMoneyStatus.DK_CPR_VALIDATION_ERROR
    );
  };

  const isSSNConfirmationRequired = ({
    data: { SSNConfirmationRequired }
  }: Pick<WizardContext, 'data'>) => !!SSNConfirmationRequired;

  const hideRMFCompletion = ({
    data: { cashierFlow }
  }: {
    data: { cashierFlow: CashierFlowType; rmfTargetToJump?: string };
  }): boolean => {
    if (
      [
        CashierFlowType.FirstWithdraw,
        CashierFlowType.FirstP2pTransfer,
        CashierFlowType.FirstDeposit
      ].includes(cashierFlow)
    ) {
      return true;
    }
    return isStandaloneRmf;
  };

  return {
    [Guard.CheckRMSuspended]: checkRMSuspended,
    [Guard.CheckUserFlags]: checkUserFlags,
    [Guard.DoesJumpToFeatureStep]: doesJumpToFeatureStep,
    [Guard.PhoneVerificationCheck]: phoneVerificationCheck,
    [Guard.RmfChallengeQuestionsCheck]: rmfChallengeQuestionsCheck,
    [Guard.RmfDobEnabled]: rmfDobEnabled,
    [Guard.ShowCitizenship]: showCitizenship,
    [Guard.ShowPendingVerification]: showPendingVerification,
    [Guard.SignupDepositEnabled]: signupDepositEnabled,
    [Guard.PhoneCollectionCheck]: phoneCollectionCheck,
    [Guard.LimitsSelected]: showLimitsScreen,
    [Guard.SingleSignUpEnabled]: singleSignUpEnabled,
    [Guard.ShowSumSubFirst]: showSumSubFirst,
    [Guard.CheckSumSubVerification]: checkSumSubVerification,
    [Guard.isCashierPath]: isCashierPath,
    [Guard.CheckRetryUserFlags]: checkRetryUserFlags,
    [Guard.CheckIsDEUserRMOK]: checkIsDEUserRMOK,
    [Guard.RecheckUserFlags]: checkUserFlags,
    [Guard.ShowDKVerificationScreen]: showDKVerificationScreen,
    [Guard.IsRetryScreenEnabled]: retryScreenEnabled,
    [Guard.UserStatusInSpain]: userStatusInSpain,
    [Guard.CheckUserAccountStatus]: checkUserAccountStatus,
    [Guard.checkPoker]: checkPoker,
    [Guard.checkCasino]: checkCasino,
    [Guard.showSGScreen]: shouldShowSGScreen,
    [Guard.CheckSGCScreenAndSelection]: checkSGCScreenAndSelection,
    [Guard.StandaloneRmf]: standaloneRMF,
    [Guard.HideRMFCompletion]: hideRMFCompletion,
    [Guard.CheckEKYCFailed]: checkEKYCFailed,
    [Guard.CheckEKYCRetry]: checkEKYCRetry,
    [Guard.WrmfKBA]: kbaScreenEnabled,
    [Guard.doesJumpFromDepositProcess]: doesJumpFromDepositProcess,
    [Guard.ShowWelcomeBonusES]: showWelcomeBonusES,
    [Guard.TooManyEKYCRequests]: tooManyEKYCRequests,
    [Guard.CheckMobilePhoneVerified]: checkMobilePhoneVerified,
    [Guard.MitIdDataPresent]: mitIdDataPresent,
    [Guard.StandaloneRMFMitIdDataPresent]: standaloneRMFMitIdDataPresent,
    [Guard.ShouldShowValidationScreenDK]: shouldShowValidationScreenDK,
    [Guard.IsSSNConfirmationRequired]: isSSNConfirmationRequired
  };
};

export default useRMFGuards;
